import React from "react";
import { Alert, Badge, Button, Pagination } from "antd";
import { CheckCircleFilled, ExclamationCircleFilled } from "@ant-design/icons";
import dayjs from "dayjs";
import CommonButton from "../../common/button/button.js";
import Table from "../../common/table";
import { useAuth } from "../AuthProvider.js";
import { canReviewAccountEnrollment } from "../AccountEnrollment/accountEnrollmentAccess.js";
import {
  getAccountEnrollmentRequests,
  getEnrollmentErrorMessage,
} from "../AccountEnrollment/accountEnrollmentService.js";
import AccountEnrollmentReviewModal from "./AccountEnrollmentReviewModal.js";
import "../AccountEnrollment/AccountEnrollment.css";

const DATE_FORMAT = "MM-DD-YYYY hh:mm A";
const DEFAULT_SORT = { field: "createdAt", order: "desc" };
const STATUS_FILTERS = [
  { value: "Pending", label: "Pending" },
  {
    value: "Approved",
    label: "Approved",
    icon: <CheckCircleFilled style={{ color: "#27a907" }} />,
  },
  {
    value: "Rejected",
    label: "Rejected",
    icon: <ExclamationCircleFilled style={{ color: "#ff0000" }} />,
  },
];

// Server-side pagination, sorting and search — same structure as
// AccountUpdateRequestsTab so the two approval queues behave identically.
const AccountEnrollmentTab = ({ searchKeyword = "" }) => {
  const { user } = useAuth();
  const [rows, setRows] = React.useState([]);
  const [status, setStatus] = React.useState("Pending");
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const [totalCount, setTotalCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [selectedRequestId, setSelectedRequestId] = React.useState(null);
  const [sortState, setSortState] = React.useState(DEFAULT_SORT);
  const [debouncedSearch, setDebouncedSearch] = React.useState(searchKeyword);
  const [refreshKey, setRefreshKey] = React.useState(0);

  const canReview = canReviewAccountEnrollment(user);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(searchKeyword);
      setPage(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchKeyword]);

  React.useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const result = await getAccountEnrollmentRequests({
          status,
          page,
          pageSize,
          search: debouncedSearch || "",
          sortField: sortState?.field || DEFAULT_SORT.field,
          sortOrder: sortState?.order || DEFAULT_SORT.order,
        });
        if (!active) return;
        setRows(result.data);
        setTotalCount(result.totalCount);
      } catch (requestError) {
        if (!active) return;
        setRows([]);
        setTotalCount(0);
        setError(
          getEnrollmentErrorMessage(
            requestError,
            "Unable to load account enrollment requests.",
          ),
        );
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [debouncedSearch, page, pageSize, refreshKey, sortState, status]);

  const columns = React.useMemo(
    () => [
      {
        headerName: "Account",
        field: "AccountName",
        minWidth: 220,
        cellRenderer: (params) => params.data?.AccountName || "—",
      },
      {
        headerName: "Distributor",
        field: "DistributorName",
        sortable: false,
        minWidth: 200,
        cellRenderer: (params) => params.data?.DistributorName || "—",
      },
      {
        headerName: "Requester",
        field: "RequesterName",
        minWidth: 180,
        cellRenderer: (params) => params.data?.RequesterName || "—",
      },
      {
        headerName: "Submitted",
        field: "createdAt",
        minWidth: 190,
        cellRenderer: (params) =>
          params.data?.createdAt
            ? dayjs(params.data.createdAt).format(DATE_FORMAT)
            : "—",
      },
      {
        headerName: "Reviewer",
        field: "ReviewerName",
        sortable: false,
        minWidth: 170,
        cellRenderer: (params) => params.data?.ReviewerName || "—",
      },
      {
        headerName: "Reviewed",
        field: "ReviewedAt",
        minWidth: 190,
        cellRenderer: (params) =>
          params.data?.ReviewedAt
            ? dayjs(params.data.ReviewedAt).format(DATE_FORMAT)
            : "—",
      },
      {
        headerName: "Action",
        field: "Action",
        sortable: false,
        filter: false,
        minWidth: 130,
        cellRenderer: (params) => (
          <CommonButton
            variant="table"
            subVariant="success"
            style={{ minWidth: "80px" }}
            onClick={() => setSelectedRequestId(params.data?.ID)}
          >
            {canReview && params.data?.Status === "Pending" ? "Review" : "View"}
          </CommonButton>
        ),
      },
    ],
    [canReview],
  );

  return (
    <div className="account-enrollment-tab">
      <div className="account-enrollment-tab-toolbar">
        {STATUS_FILTERS.map((filter) => (
          <Button
            key={filter.value}
            ghost={status !== filter.value}
            style={{ color: "#444", borderColor: "#444" }}
            onClick={() => {
              setStatus(filter.value);
              setPage(1);
            }}
          >
            {filter.icon ? <Badge count={filter.icon} /> : null}
            {filter.label}
          </Button>
        ))}
      </div>
      {error ? <Alert type="error" showIcon message={error} /> : null}
      <Table
        rowData={rows}
        columnDefs={columns}
        loading={loading}
        pagination={false}
        serverSideSort
        sortState={sortState}
        onSortChange={(next) => {
          setSortState(next || DEFAULT_SORT);
          setPage(1);
        }}
      />
      <div className="account-enrollment-pagination">
        <Pagination
          current={page}
          pageSize={pageSize}
          total={totalCount}
          showSizeChanger
          pageSizeOptions={[10, 20, 50, 100]}
          showTotal={(total) => `${total} request${total === 1 ? "" : "s"}`}
          onChange={(nextPage, nextPageSize) => {
            if (nextPageSize !== pageSize) {
              setPageSize(nextPageSize);
              setPage(1);
            } else {
              setPage(nextPage);
            }
          }}
        />
      </div>
      <AccountEnrollmentReviewModal
        canReview={canReview}
        onClose={() => setSelectedRequestId(null)}
        onReviewed={() => setRefreshKey((value) => value + 1)}
        open={Boolean(selectedRequestId)}
        requestId={selectedRequestId}
      />
    </div>
  );
};

export default AccountEnrollmentTab;
